import React from 'react';
import { useNavigate } from 'react-router';
import { EventBus } from '../../contexts/event-bus';
import { HomeFlyoutTypes } from '../../features/home/flyouts/types';
import { SETTINGS_ROUTES } from '../../features/settings/router/settings-routes';
import { useAppDispatch, useAppSelector } from '../../hooks/store-hooks';
import { ROUTES } from '../../router/routes';
import { setWorkspacePanelVisibility } from '../../state/slices/workspace-slice';
import { focusExternalWindow } from '../multi-window/ExternalWindowPortal';
import { WorkspaceWindowPanelType } from '../multi-window/types';
import { Command } from './types';
import { useFilterCommands } from './use-filter-commands';
import { useLayerCommands } from './use-layer-commands';
import { useViewCommands } from './use-view-commands';

export function useCommands(): Command[] {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const panels = useAppSelector((x) => x.workspace.panels);
    const windows = useAppSelector((x) => x.workspace.windows);
    const layerCommands = useLayerCommands();
    const viewCommands = useViewCommands();
    const filterCommands = useFilterCommands();

    return React.useMemo<Command[]>(() => {
        // Panels that can be detached into an external window: focus the window if it exists,
        // otherwise toggle the in-app panel.
        const windowedPanelCommand = (
            id: string,
            title: string,
            keywords: string[],
            shortcut: string,
            shortcutLabel: string,
            panel: keyof typeof panels,
            windowType: WorkspaceWindowPanelType
        ): Command => ({
            id,
            title,
            keywords,
            group: 'Panels',
            shortcut,
            shortcutLabel,
            run: () => {
                const windowId = windows.find((w) => w.panels.some((p) => p.type === windowType))?.id;
                if (windowId !== undefined && focusExternalWindow(windowId)) {
                    return;
                }
                dispatch(setWorkspacePanelVisibility({ panel, visible: !panels[panel] }));
            }
        });

        const flyoutCommand = (
            id: string,
            title: string,
            keywords: string[],
            shortcut: string,
            shortcutLabel: string,
            flyout: HomeFlyoutTypes
        ): Command => ({
            id,
            title,
            keywords,
            group: 'Panels',
            shortcut,
            shortcutLabel,
            run: () => EventBus.fire('open-flyout', { type: flyout })
        });

        return [
            {
                id: 'command-center',
                title: 'Open Command Center',
                keywords: ['command', 'palette', 'search everything'],
                group: 'Panels',
                shortcutLabel: 'Alt+K',
                run: () => EventBus.fire('open-command-center', null)
            },
            {
                id: 'constraints-list',
                title: 'Open Constraints List',
                keywords: ['constraints', 'fca', 'flight constrained area', 'airport delays', 'reroute', 'advisory'],
                group: 'Panels',
                shortcut: 'alt+c',
                shortcutLabel: 'Alt+C',
                run: () => EventBus.fire('toggle-constraints-list', null)
            },
            windowedPanelCommand(
                'route-analyzer',
                'Open Route Analyzer',
                ['route', 'analyzer', 'analyze', 'reroute'],
                'alt+r',
                'Alt+R',
                'routeAnalyzer',
                WorkspaceWindowPanelType.routeAnalyzer
            ),
            {
                id: 'search',
                title: 'Open Search',
                keywords: ['search', 'find', 'flight', 'airport', 'navaid', 'fix', 'airway'],
                group: 'Panels',
                shortcut: 'alt+f',
                shortcutLabel: 'Alt+F',
                run: () => EventBus.fire('open-flyout', { type: HomeFlyoutTypes.search })
            },
            windowedPanelCommand(
                'fleet-monitor',
                'Open Fleet Monitor',
                ['fleet', 'monitor', 'aircraft'],
                'alt+m',
                'Alt+M',
                'fleetMonitor',
                WorkspaceWindowPanelType.fleetMonitor
            ),
            windowedPanelCommand(
                'airport-monitor',
                'Open Airport Monitor',
                ['airport', 'monitor'],
                'alt+a',
                'Alt+A',
                'airportMonitor',
                WorkspaceWindowPanelType.airportMonitor
            ),
            windowedPanelCommand(
                'event-list',
                'Open Event List',
                ['event', 'list', 'alerts'],
                'alt+e',
                'Alt+E',
                'eventList',
                WorkspaceWindowPanelType.eventList
            ),
            windowedPanelCommand(
                'legend',
                'Open Legend',
                ['legend', 'map legend', 'colors'],
                'alt+g',
                'Alt+G',
                'legend',
                WorkspaceWindowPanelType.mapLegend
            ),
            flyoutCommand('views-flyout', 'Open Views', ['views', 'view'], 'alt+v', 'Alt+V', HomeFlyoutTypes.views),
            flyoutCommand('layers-flyout', 'Open Layers', ['layers', 'map layers'], 'alt+l', 'Alt+L', HomeFlyoutTypes.layers),
            flyoutCommand('weather-flyout', 'Open Weather', ['weather', 'products', 'radar'], 'alt+w', 'Alt+W', HomeFlyoutTypes.weather),
            flyoutCommand(
                'filters-flyout',
                'Open Aircraft Filters',
                ['filters', 'aircraft filters', 'filter'],
                'alt+i',
                'Alt+I',
                HomeFlyoutTypes.filters
            ),
            {
                id: 'settings',
                title: 'Open Settings',
                keywords: ['settings', 'preferences', 'general', 'options', 'configuration'],
                group: 'Panels',
                shortcut: 'alt+s',
                shortcutLabel: 'Alt+S',
                run: () => {
                    if (window.location.pathname.startsWith(ROUTES.SETTINGS)) {
                        navigate(-1);
                    }
                    navigate(`${ROUTES.SETTINGS}/${SETTINGS_ROUTES.general.index}`);
                }
            },
            ...layerCommands,
            ...viewCommands,
            ...filterCommands
        ];
    }, [dispatch, navigate, panels, windows, layerCommands, viewCommands, filterCommands]);
}
